import { z } from "zod";
import { hexColor, isoDate, isoDateTime, moneyMinor, optionalText, text, uuid, wallTime } from "./common";
import { weekday } from "./org";

export const DOCTOR_TYPES = ["general_dentist", "specialist", "consultant", "visiting", "part_time", "locum"] as const;

export const doctorInput = z.object({
  staffMemberId: uuid,
  doctorType: z.enum(DOCTOR_TYPES),
  title: optionalText(20),
  registrationNumber: optionalText(60),
  registrationCouncil: optionalText(200),
  qualifications: optionalText(500),
  bio: optionalText(4000),
  defaultAppointmentMinutes: z.number().int().min(5).max(480),
  consultationFeeMinor: moneyMinor.nullable().default(null),
  acceptsOnlineBooking: z.boolean().default(false),
  calendarColor: hexColor.nullable().default(null),
  isActive: z.boolean().default(true),
  branchIds: z.array(uuid).min(1, "Choose at least one branch").max(200),
  specialtyIds: z.array(uuid).max(20).default([]),
  primarySpecialtyId: uuid.nullable().default(null),
});
export const updateDoctorInput = doctorInput.omit({ staffMemberId: true }).extend({ version: z.number().int().min(1) });
export type DoctorInput = z.infer<typeof doctorInput>;

export interface Doctor {
  id: string;
  staffMemberId: string;
  fullName: string;
  doctorType: (typeof DOCTOR_TYPES)[number];
  title: string | null;
  registrationNumber: string | null;
  registrationCouncil: string | null;
  qualifications: string | null;
  bio: string | null;
  defaultAppointmentMinutes: number;
  consultationFeeMinor: number | null;
  acceptsOnlineBooking: boolean;
  calendarColor: string | null;
  isActive: boolean;
  branchIds: string[];
  specialties: { id: string; name: string; isPrimary: boolean }[];
  version: number;
}

export const specialtyInput = z.object({ name: text(100), isActive: z.boolean().default(true) });

/* ----------------------------------------------------------------- availability */

export const availabilityRuleInput = z
  .object({
    branchId: uuid,
    weekday,
    startTime: wallTime,
    endTime: wallTime,
    effectiveFrom: isoDate,
    effectiveTo: isoDate.nullable().default(null),
    roomId: uuid.nullable().default(null),
    chairId: uuid.nullable().default(null),
    notes: optionalText(500),
  })
  .refine((r) => r.endTime > r.startTime, { message: "End must be after start", path: ["endTime"] })
  .refine((r) => !r.effectiveTo || r.effectiveTo >= r.effectiveFrom, { message: "End date is before start date", path: ["effectiveTo"] });
export type AvailabilityRuleInput = z.infer<typeof availabilityRuleInput>;
export interface AvailabilityRule extends AvailabilityRuleInput {
  id: string;
  doctorId: string;
}

export const availabilityExceptionInput = z
  .object({
    kind: z.enum(["leave", "unavailable", "extra"]),
    branchId: uuid.nullable().default(null),
    startsAt: isoDateTime,
    endsAt: isoDateTime,
    reason: optionalText(500),
  })
  .refine((e) => new Date(e.endsAt) > new Date(e.startsAt), { message: "End must be after start", path: ["endsAt"] })
  .refine((e) => e.kind !== "extra" || e.branchId !== null, { message: "Extra availability needs a branch", path: ["branchId"] });
export type AvailabilityExceptionInput = z.infer<typeof availabilityExceptionInput>;
export interface AvailabilityException extends AvailabilityExceptionInput {
  id: string;
  doctorId: string;
}

export const workingWindowsQuery = z.object({
  from: isoDate,
  to: isoDate,
  branchId: uuid.optional(),
});

/** A concrete period when a doctor is working at a branch (rules + extras − leave − closures). */
export interface WorkingWindow {
  branchId: string;
  start: string;
  end: string;
  source: "rule" | "extra";
  roomId: string | null;
  chairId: string | null;
}
